import Button from 'src/components/form/Button';
import React, { useState } from 'react';
import { Modal, ModalSubTitle } from './comps';
import styled from 'styled-components';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { queryCache } from 'react-query';
import MaterialInput from 'src/components/form/MaterialInput';
import { Guard } from './Guard';
import { Strategy, strategyOptions } from 'src/api/types';
import CSelect from '../form/Select';
import { InputLabel, Poppins, Spacer } from 'src/common';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { useUpsertScenarioMutation } from 'src/graphql/_generated';
import { useCache } from 'src/graphql';

const StyledModal = styled(Modal)`
  width: 566px;
  min-height: 200px;

  .success {
    min-height: 20px;
    display: flex;
    align-items: center;
    text-align: center;
  }
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.colors.error};
  font-size: 14px;
`;

const ErrorLabel = styled.div`
  margin-top: 15px;
  text-align: center;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.error};
  font-weight: 400;
`;

interface ManageRiskModalProps {
  onClose: () => void;
  scenarioDetails: {
    id: string;
    managementStrategy?: string | null;
    managementNote?: string | null;
  };
  subTitle?: {
    title: string;
    value: string;
  };
}

export const ManageRiskModal: React.FC<ManageRiskModalProps> = ({ onClose, scenarioDetails, subTitle }) => {
  const [isManagementNoteTouched, setIsManagementNoteTouched] = useState(false);
  const [isCloseRequested, setIsCloseRequested] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { refetchQuery } = useCache();

  const validationSchema = Yup.object().shape({
    managementStrategy: Yup.string()
      .oneOf(['Treat', 'Transfer', 'Tolerate', 'Terminate'], 'Invalid management strategy selected')
      .nullable(),
    managementNote: Yup.string().test('len', 'Management Note too long (maximum 2048 characters)', (val) => {
      if (val && val.length > 2048) {
        setIsManagementNoteTouched(true);
        return false;
      }
      return true;
    }),
  });

  const handleClose = () => onClose();

  const [upsertScenario] = useUpsertScenarioMutation({
    onCompleted: () => {
      queryCache.invalidateQueries();
      refetchQuery({ include: ['scenario'] });
      handleClose();
    },
    onError: (error: any) => {
      setErrorMessage(error.msg || 'Something went wrong');
    },
  });

  const {
    values,
    errors,
    handleBlur,
    touched,
    isSubmitting,
    handleChange,
    handleSubmit,
    isValid,
    dirty,
    setFieldValue,
  } = useFormik({
    initialValues: {
      managementStrategy: scenarioDetails?.managementStrategy || '',
      managementNote: scenarioDetails?.managementNote || '',
    },
    validationSchema,
    onSubmit: (values) => {
      return upsertScenario({
        variables: {
          scenarioData: { ...values, id: scenarioDetails?.id },
        },
      });
    },
  });

  return (
    <Guard
      onCancel={() => setIsCloseRequested(false)}
      onClose={onClose}
      isOpen={isCloseRequested}
      isDirty={dirty}
      modalComponent={
        <StyledModal isOpen onRequestClose={() => setIsCloseRequested(true)}>
          <Poppins className="m-title" px={28}>
            Risk Management Strategy
          </Poppins>
          {subTitle && <ModalSubTitle title={subTitle.title} value={subTitle.value} />}
          <Spacer $px={20} />

          <form onSubmit={handleSubmit} data-cy="rms">
            <InputLabel>MANAGEMENT STRATEGY</InputLabel>
            <CSelect
              name="managementStrategy"
              onChange={(o: { value: Strategy }) => setFieldValue('managementStrategy', o.value)}
              onBlur={handleBlur}
              value={strategyOptions.find((o) => o.value === values.managementStrategy)}
              placeholder="Control strategy"
              css="width: 100%;"
              options={strategyOptions}
              inputId="control-strategy"
            />
            {errors.managementStrategy && touched.managementStrategy && (
              <ErrorText>{errors.managementStrategy}</ErrorText>
            )}
            <Spacer $px={20} />

            <InputLabel>NOTE</InputLabel>
            <MaterialInput
              id="managementNote"
              name="managementNote"
              value={values.managementNote}
              onChange={handleChange}
              onBlur={handleBlur}
              multiline
              fullWidth
              placeholder="Enter management note"
              rows={3}
              data-cy="note-input"
            />

            {errors.managementNote && (touched.managementNote || isManagementNoteTouched) && (
              <ErrorText>{errors.managementNote}</ErrorText>
            )}
            <Spacer $px={20} />
            <Button
              color="primary"
              disabled={!isValid || isSubmitting}
              type="submit"
              primary
              css="width: 100%;"
              data-cy="add-btn"
              onClick={() =>
                mpEvent(MPEvents.ButtonClick, {
                  button: 'Submit',
                  modal: 'Manage risk modal',
                  tags: ['SCENARIO'],
                })
              }
            >
              {isSubmitting
                ? 'Saving... Please wait'
                : scenarioDetails?.managementStrategy == null && scenarioDetails?.managementNote == null
                  ? 'Add'
                  : 'Update'}
            </Button>

            {errorMessage && <ErrorLabel>{errorMessage}</ErrorLabel>}
          </form>
        </StyledModal>
      }
    />
  );
};
